import { useEffect, useMemo, useRef } from 'react';
import styles from './LineDotMap.module.less'
import { useChartConfig } from '../CoreChart/CoreChart';
// import { DATA_DOT_SIZE } from '../FloatLine/FloatLine';
import { EChartsInstance } from 'echarts-for-react';
import cls from 'classnames'
import Player from 'xgplayer/es/player';
import { useTranslateDimension } from '../../../VideoAnalysis';

const SCALE_DATA_DOT_SIZE = 5

interface Props {
  project: ProjectModels.ProjectDetail | ProjectModels.ProjectDetailV3
  parentRect: DOMRect | null
  visible?: boolean
  chartInstance: EChartsInstance | null
  lineMoveX: number
  dashLineMoveX: number
  videoInstance: Player | null
  videoSize: { width: string, height: string, left: string, top: string }
  duration: number
  axisCount?: number
  selectedDimension: string[]
  onXAxisDotsChange: (xAxisDots: Array<{ x: number, dots: Dot[] }>) => void
  onActiveXAxisIndexChange?: (index: number) => void
}


type DotType = 'attension' | 'emotion' | 'recognition'

type Dot = {
  name: string;
  value: number; x: number; y: number,
  type: DotType
}

type Label = {
  y: number,
  value: number,
  type: DotType,
}

function isSameLabels(prev: Label[], next: Label[]) {
  if (prev.length !== next.length) {
    return false
  }
  return prev.every((label, index) => {
    const nextLabel = next[index]
    return label.y === nextLabel.y
      && label.value === nextLabel.value
      && label.type === nextLabel.type
  })
}

function isSameDots(prev: Dot[], next: Dot[]) {
  if (prev.length !== next.length) {
    return false
  }
  return prev.every((dot, index) => {
    const nextDot = next[index]
    return dot.name === nextDot.name
      && dot.value === nextDot.value
      && dot.x === nextDot.x
      && dot.y === nextDot.y
      && dot.type === nextDot.type
  })
}

function isSameXAxisDots(
  prev: Array<{ x: number, dots: Dot[], labels: Label[] }>,
  next: Array<{ x: number, dots: Dot[], labels: Label[] }>
) {
  if (prev.length !== next.length) {
    return false
  }
  return prev.every((item, index) => {
    const nextItem = next[index]
    return item.x === nextItem.x
      && isSameDots(item.dots, nextItem.dots)
      && isSameLabels(item.labels, nextItem.labels)
  })
}

/** 值标签的高度 */
const DOT_VALUE_LABEL_HEIGHT = 20
const DOT_VALUE_LABEL_WIDTH = 30
const DOT_VALUE_LABEL_MARGIN = 20

function LineDotMap(props: Props) {
  const { parentRect, } = props
  const { config } = useChartConfig(props.project.detailMetrics)
  const chartOffsetConfigGrid = config.grid as any
  const lastXAxisDotsRef = useRef<Array<{ x: number, dots: Dot[], labels: Label[] }>>([])
  const lastActiveXAxisIndexRef = useRef(-1)
  const { EYE_MOVEMENT_RATIO, COGNITIVE_ENGAGEMENT, EMOTION_RECOGNITION } = useTranslateDimension()
  const dotTypeList = useMemo(() => [
    { key: EYE_MOVEMENT_RATIO, type: 'attension' },
    { key: COGNITIVE_ENGAGEMENT, type: 'recognition' },
    { key: EMOTION_RECOGNITION, type: 'emotion' },
  ], [COGNITIVE_ENGAGEMENT, EMOTION_RECOGNITION, EYE_MOVEMENT_RATIO])

  const xAxisDots = useMemo(() => {
    if (!props.chartInstance || !parentRect) { return [] as Array<{ x: number, dots: Dot[], labels: Label[] }> }
    const xAxisLength = props.project.frameInfos.length
    const xAxisPoints = Array.from({ length: xAxisLength }, (_, index) => {
      const point = props.chartInstance.convertToPixel({ seriesIndex: 0 }, [index, 0])
      return point
    })
    const hasNull = xAxisPoints.some(p => !p)
    if (hasNull) { return [] as Array<{ x: number, dots: Dot[], labels: Label[] }> }
    const containerHeight = parentRect.height
    /** chartHeight 是指 y 轴的高度 */
    const yAxisHeight = containerHeight - chartOffsetConfigGrid.bottom - chartOffsetConfigGrid.top
    /** y 轴被平均分成 100 份 */
    const chartRatio = yAxisHeight / 100
    const dots = xAxisPoints.map((xAxisPoint, xIndex) => {
      const dotValues = (config.series as any[]).map((s, index) => {
        const types: DotType[] = ['attension', 'recognition', 'emotion']
        const frame = props.project.frameInfos[xIndex]?.frame
        if (frame) {
          return {
            name: s.name,
            value: s.data[xIndex] || 0,
            type: types[index]
          }
        } else {
          return {
            name: '',
            value: 0,
            type: types[index]
          }
        }
      })
      const dataDots = dotValues.map((d) => {
        /**
         * NOTE: 卧槽，这里为啥会有  1.2 的偏差，我也不知道，因为值都是 chart 计算给出来的，可能是计算出来的精度问题吧，、
         * 也可能修改了 chart 配置中的 grid 的 containLabel 导致的。这里先加上吧。
         */
        return {
          ...d,
          y: d.value * chartRatio + SCALE_DATA_DOT_SIZE * 0.5 - 1.2,
          x: -SCALE_DATA_DOT_SIZE * 0.5 + 1.2 + xAxisPoint[0],
        }
      })
      const sortDotValues = dotValues.sort((a, b) => {
        return b.value - a.value
      })
      const dataLabels = sortDotValues.reduce((prev, d) => {
        const last = prev[prev.length - 1]
        const willBeY = d.value * chartRatio - DOT_VALUE_LABEL_HEIGHT * 0.5 - 1.2
        if (last) {
          const lastY = last.y
          const isOverlay = willBeY + DOT_VALUE_LABEL_HEIGHT > lastY || willBeY === lastY
          const offset = isOverlay ? willBeY + DOT_VALUE_LABEL_HEIGHT - lastY : 0
          return [
            ...prev,
            {
              value: d.value,
              type: d.type,
              y: isOverlay ? willBeY - offset : willBeY
            }
          ]
        }
        return [
          ...prev,
          {
            value: d.value,
            type: d.type,
            y: willBeY
          }
        ]
      }, [] as Label[])
      return { x: xAxisPoint[0], dots: dataDots, labels: dataLabels }
    })
    const needFilterDotTypeList = dotTypeList.filter(t => props.selectedDimension.includes(t.key)).map(s => s.type)
    const filterDots = dots.map(d => {
      return {
        ...d,
        dots: d.dots.filter(dot => needFilterDotTypeList.includes(dot.type)),
        labels: d.labels.filter(label => needFilterDotTypeList.includes(label.type)),
      }
    })
    return filterDots
  }, [chartOffsetConfigGrid.bottom, chartOffsetConfigGrid.top, config.series, dotTypeList, parentRect, props.chartInstance, props.project.frameInfos, props.selectedDimension])

  const activeXAxisIndex = useMemo(() => {
    if (!props.visible) {
      return -1
    }
    if (xAxisDots.length === 0) {
      return -1
    }
    const dashMoveX = props.dashLineMoveX
    let nearestIndex = 0
    let nearestDistance = Math.abs(xAxisDots[0].x - dashMoveX)
    for (let i = 1; i < xAxisDots.length; i++) {
      const distance = Math.abs(xAxisDots[i].x - dashMoveX)
      if (distance < nearestDistance) {
        nearestDistance = distance
        nearestIndex = i
      }
    }
    return nearestIndex
  }, [props.visible, props.dashLineMoveX, props.duration, props.axisCount, xAxisDots])

  useEffect(() => {
    if (isSameXAxisDots(lastXAxisDotsRef.current, xAxisDots)) {
      return
    }
    lastXAxisDotsRef.current = xAxisDots
    props.onXAxisDotsChange(xAxisDots)
  }, [xAxisDots])

  useEffect(() => {
    if (lastActiveXAxisIndexRef.current === activeXAxisIndex) {
      return
    }
    lastActiveXAxisIndexRef.current = activeXAxisIndex
    props.onActiveXAxisIndexChange?.(activeXAxisIndex)
  }, [activeXAxisIndex])

  const activeXAxis = activeXAxisIndex >= 0 ? xAxisDots[activeXAxisIndex] : undefined

  return <>
    {props.visible && activeXAxis && <div key='active-dots' className={styles.xAxis} style={{ left: activeXAxis.x }}>
      {
        activeXAxis.dots.map((dot, index) => {
          return <div key={index} className={cls(styles.dot,
            styles.cross,
            dot.type === 'attension' && styles.attension,
            dot.type === 'emotion' && styles.emotion,
            dot.type === 'recognition' && styles.recognition
          )}
            style={{
              width: SCALE_DATA_DOT_SIZE,
              height: SCALE_DATA_DOT_SIZE,
              left: -SCALE_DATA_DOT_SIZE * 0.5,
              bottom: dot.y + chartOffsetConfigGrid.bottom - SCALE_DATA_DOT_SIZE * 0.8
            }}>
          </div>
        })
      }
    </div>}
    {props.visible && activeXAxis && <div key='active-label' className={styles.xAxisLabel} style={{
      width: DOT_VALUE_LABEL_WIDTH,
      height: '100%',
      left: activeXAxis.x + SCALE_DATA_DOT_SIZE + DOT_VALUE_LABEL_MARGIN
    }}>
      {
        activeXAxis.labels.map((label, index) => {
          return <div key={index} className={cls(styles.label,
            styles.cross,
            label.type === 'attension' && styles.attension,
            label.type === 'emotion' && styles.emotion,
            label.type === 'recognition' && styles.recognition
          )}
            style={{
              width: DOT_VALUE_LABEL_WIDTH,
              height: DOT_VALUE_LABEL_HEIGHT,
              left: -DOT_VALUE_LABEL_WIDTH * 0.5,
              bottom: label.y + chartOffsetConfigGrid.bottom
            }}>
            {label.value}
          </div>
        })
      }
    </div>}
  </>
}

export default LineDotMap
